import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { transactionAPI, fieldSettingAPI } from '../services/api';
import { Search, Filter, Plus, Download } from 'lucide-react';
import { formatDate } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import SearchHistory from '../components/SearchHistory';
import { useToast } from '../contexts/ToastContext';
import Button from '../components/Button';
import RegistrationDateRangeFilter from '../components/RegistrationDateRangeFilter';
import * as XLSX from 'xlsx';
import { FIELD_LABEL_DEFAULTS as FL } from '../utils/fieldLabelDefaults';

const Contracts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: '',
    tombType: '',
    startDate: '',
    endDate: '',
    excludeType: '해약,이장', // 해약·이장 계약은 전용 메뉴에서 조회
  });
  const [searchInput, setSearchInput] = useState('');
  const [tombType, setTombType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const searchHistoryRef = useRef(null);
  const searchInputRef = useRef(null);
  const isSearchHistoryVisible = useRef(false); // 🔧 추가: 안정적인 상태 추적
  const [showAdditionalBurialModal, setShowAdditionalBurialModal] = useState(false);

  // 필드 설정 조회
  const { data: fieldSettings } = useQuery({
    queryKey: ['fieldSettings', user?.cemeteryId],
    queryFn: () => fieldSettingAPI.getFieldSettingsByCemetery(user?.cemeteryId),
    select: (response) => response.data.data,
    enabled: !!user?.cemeteryId
  });

  // 필드 라벨 가져오기 헬퍼 함수
  const getFieldLabel = (fieldCode, defaultLabel) => {
    if (!fieldSettings || !fieldSettings.fields) return defaultLabel;
    const field = fieldSettings.fields.find(f => f.field_code === fieldCode);
    return field?.custom_label || field?.label || defaultLabel;
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['contracts', filters],
    queryFn: () => {
      console.log('=== 계약 목록 API 호출 ===');
      console.log('filters:', filters);
      console.log('user:', user);
      return transactionAPI.getAll(filters);
    },
    select: (response) => ({
      ...response.data.pagination,
      data: response.data.transactions?.map(contract => {
        const uniqueAcquaintances = [];
        const seenKeys = new Set();

        const addAcquaintance = (acquaintance) => {
          if (!acquaintance) return;
          const key = acquaintance.id ?? `${acquaintance.name || ''}-${acquaintance.phone || ''}-${acquaintance.resident_number || ''}`;
          if (seenKeys.has(key)) return;
          seenKeys.add(key);
          uniqueAcquaintances.push(acquaintance);
        };

        // 1. Transaction에 직접 연결된 연고자 (예정지 계약 등)
        if (contract.acquaintances && Array.isArray(contract.acquaintances)) {
          contract.acquaintances.forEach(acquaintance => {
            addAcquaintance(acquaintance);
          });
        }

        // 2. Dead를 통한 연고자 (기존 방식)
        contract.deads?.forEach(dead => {
          addAcquaintance(dead.acquaintance1);
          addAcquaintance(dead.acquaintance2);
        });

        // 주연고자(is_primary_contact/is_primary)가 있으면 우선 배치
        uniqueAcquaintances.sort((a, b) => {
          const aPrimary = a?.is_primary_contact ?? a?.is_primary ?? false;
          const bPrimary = b?.is_primary_contact ?? b?.is_primary ?? false;
          if (aPrimary === bPrimary) return 0;
          return aPrimary ? -1 : 1;
        });

        return {
          ...contract,
          dead: contract.deads?.[0] || null,
          acquaintances: uniqueAcquaintances
        };
      }) || []
    }),
    keepPreviousData: true,
  });

  // 프론트엔드에서 최근 검색어 저장을 수행하지 않음 (백엔드에서 단일 처리)

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // 필터 변경 시 첫 페이지로
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleQuery = () => {
    const searchTerm = searchInput.trim();
    // 최근 검색어 저장은 백엔드에서 처리 (중복 방지)

    setFilters(prev => ({
      ...prev,
      search: searchInput,
      tombType,
      startDate,
      endDate,
      page: 1,
      excludeType: '해약,이장'
    }));
  };

  // 검색 입력 변경 시
  const handleSearchInputChange = (value) => {
    setSearchInput(value);
  };

  // 검색 히스토리에서 검색어 선택
  const handleSearchSelect = (searchTerm) => {
    console.log('🔍 검색어 선택:', searchTerm);
    setSearchInput(searchTerm);
    setFilters(prev => ({
      ...prev,
      search: searchTerm,
      page: 1,
      excludeType: '해약,이장'
    }));
    isSearchHistoryVisible.current = false; // 🔧 상태 업데이트
    setShowSearchHistory(false);
    setShowSearchHistory(false);
  };

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      console.log('🔍 외부 클릭 감지:', event.target);
      console.log('🔍 현재 검색어 결과 표시 상태:', isSearchHistoryVisible.current);
      
      // 🔧 검색어 결과 영역 클릭 시 무시
      if (searchHistoryRef.current && searchHistoryRef.current.contains(event.target)) {
        console.log('🔍 검색어 결과 영역 클릭 - 무시');
        return;
      }
      
      // 🔧 입력 필드 내부 클릭 시 무시
      if (searchInputRef.current && searchInputRef.current.contains(event.target)) {
        console.log('🔍 입력 필드 내부 클릭 - 무시');
        return;
      }
      
      // 🔧 외부 클릭 시에만 검색어 결과 숨김
      if (isSearchHistoryVisible.current) {
        console.log('🔍 외부 클릭 - 검색어 결과 숨김');
        isSearchHistoryVisible.current = false;
        setShowSearchHistory(false);
      }
    };

    if (showSearchHistory) {
      console.log('🔍 이벤트 리스너 등록');
      // click 이벤트만 사용하여 더 안정적으로 처리
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      console.log('🔍 이벤트 리스너 제거');
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showSearchHistory]);

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    if (!data?.data || data.data.length === 0) {
      showError('다운로드할 데이터가 없습니다.');
      return;
    }

    try {
      // 엑셀 데이터 준비
      const excelData = data.data.map(contract => {
        // 묘역위치 생성
        const mausoleumNumber = contract.mausoleum ? 
          `${contract.mausoleum.complex || ''}-${contract.mausoleum.area || ''}-${contract.mausoleum.row_number || ''}-${contract.mausoleum.number || ''}` : 
          '';
        
        // 사망자 정보 (최대 2명)
        const deads = contract.deads || [];
        const dead1 = deads[0] || null;
        const dead2 = deads[1] || null;
        
        // 연고자 정보 (최대 2명)
        const acquaintances = contract.acquaintances || [];
        const acquaintance1 = acquaintances[0] || null;
        const acquaintance2 = acquaintances[1] || null;
        
        // 관리비 납부 여부 및 관리일 계산
        const managementFees = contract.managementFees || [];
        let paymentStatus = '미발생';
        let managementPeriodDisplay = '-';
        
        // 관리 기간 (묘소 정보에서 가져옴)
        const managementPeriod = parseInt(contract.mausoleum?.period) || 0;

        if (managementFees.length > 0) {
          // 가장 최신 관리비 시작일 찾기
          const sortedFees = [...managementFees].sort((a, b) =>
            new Date(b.fee_period_start) - new Date(a.fee_period_start)
          );
          const latestFee = sortedFees[0];
          const startDate = new Date(latestFee.fee_period_start);

          // 종료일 계산: 시작일 + 관리 기간(년)
          const endDate = new Date(startDate);
          endDate.setFullYear(endDate.getFullYear() + managementPeriod);

          // 관리일: 마지막 관리비 시작일 ~ 종료일
          managementPeriodDisplay = `${formatDate(latestFee.fee_period_start)} ~ ${formatDate(endDate)}`;
        
          // 납부 여부 확인
          const unpaidFees = managementFees.filter(fee => !fee.is_paid);
          if (unpaidFees.length === 0) {
            paymentStatus = '완료';
          } else {
            paymentStatus = '미납';
          }
        } else if (contract.contract_date) {
          // 관리비가 없는 경우: 계약일 ~ 계약일 + 관리 기간
          const startDate = new Date(contract.contract_date);
          const endDate = new Date(startDate);
          endDate.setFullYear(endDate.getFullYear() + managementPeriod);

          managementPeriodDisplay = `${formatDate(contract.contract_date)} ~ ${formatDate(endDate)}`;
        }
        
        return {
          [getFieldLabel('member_number', FL.member_number)]: contract.mausoleum?.member_number || '',
          [`${getFieldLabel('dead_name', FL.dead_name)}1`]: dead1?.name || '',
          [`${getFieldLabel('dead_name', FL.dead_name)}2`]: dead2?.name || '',
          [getFieldLabel('mausoleum_location', FL.mausoleum_location)]: mausoleumNumber,
          [`${getFieldLabel('acquaintance_name', FL.acquaintance_name)}1`]: acquaintance1?.name || '',
          [`${getFieldLabel('acquaintance_name', FL.acquaintance_name)}2`]: acquaintance2?.name || '',
          [getFieldLabel('registration_date', FL.registration_date)]: formatDate(contract.contract_date),
          [getFieldLabel('management_period', FL.management_period)]: managementPeriodDisplay,
          [getFieldLabel('area_size', FL.area_size)]: contract.mausoleum?.area_size ? parseFloat(contract.mausoleum.area_size).toFixed(1) : '',
          [getFieldLabel('tomb_type', FL.tomb_type)]: contract.mausoleum?.tomb_type || '',
          [getFieldLabel('number_of_tombs', FL.number_of_tombs)]: contract.mausoleum?.number_of_tombs ?? '',
          [getFieldLabel('payment_status', FL.payment_status)]: paymentStatus,
          [getFieldLabel('memo', FL.memo)]: contract.memo || ''
        };
      });

      // 워크북 생성
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '계약목록');

      // 파일 다운로드
      const fileName = `계약목록_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      success('엑셀 파일이 다운로드되었습니다.');
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      showError('엑셀 다운로드에 실패했습니다.');
    }
  };

  useEffect(() => {
    setSearchInput(filters.search);
    setTombType(filters.tombType || '');
    setStartDate(filters.startDate || '');
    setEndDate(filters.endDate || '');
  }, [filters.search, filters.tombType, filters.startDate, filters.endDate]);

  if (isLoading) {
    return <div className="loading">계약 목록을 불러오는 중...</div>;
  }

  if (error) {
    return (
      <div className="error">
        계약 목록을 불러오는 중 오류가 발생했습니다: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex-between">
        <h1 className="text-2xl font-bold text-gray-900">계약 관리</h1>
        <div className="flex items-center space-x-3">
          {hasPermission(user?.role, 'CONTRACT_CREATE') && (
            <>
              <Button
                variant="secondary"
                onClick={() => setShowAdditionalBurialModal(true)}
              >
                안치자 추가
              </Button>
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => navigate('/contracts/new')}
              >
                신규 계약
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-end">
            {/* 묘지형태 */}
            <div>
              <label className="form-label">묘지형태</label>
              <select
                value={tombType}
                onChange={e => setTombType(e.target.value)}
                className="select"
              >
                <option value="">전체</option>
                <option value="평장묘">평장묘</option>
                <option value="가족평장묘">가족평장묘</option>
                <option value="매장묘">매장묘</option>
                <option value="봉안묘">봉안묘</option>
                <option value="사용지">사용지</option>
                <option value="예정지">예정지</option>
              </select>
            </div>

            {/* 검색 입력 */}
            <div className="relative">
              <label className="form-label">검색</label>
              <div className="relative">
                {/* 🔧 검색어가 있을 때는 돋보기 아이콘 숨김 */}
                {/* {!searchInput && (
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                )} */}
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={`${getFieldLabel('dead_name', FL.dead_name)}, ${getFieldLabel('acquaintance_name', FL.acquaintance_name)}, ${getFieldLabel('member_number', FL.member_number)}`}
                  value={searchInput}
                  onChange={e => handleSearchInputChange(e.target.value)}
                  onClick={() => {
                    console.log('🔍 검색어 입력 필드 클릭');
                    isSearchHistoryVisible.current = true;
                    setShowSearchHistory(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleQuery();
                    }
                  }}
                  className="input pl-10"
                />
                {showSearchHistory && (
                  <div
                    ref={searchHistoryRef}
                    className="absolute top-full left-0 right-0 z-50 mt-1"
                    onMouseDown={(e) => {
                      console.log('🔍 SearchHistory 영역 마우스 다운');
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <SearchHistory
                      onSearchSelect={handleSearchSelect}
                      maxRecent={5}
                      maxPopular={5}
                      cemeteryId={user?.cemetery_id}
                      className="bg-white border border-gray-300 rounded-lg shadow-lg"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* 등록일자 */}
            <RegistrationDateRangeFilter
              label={getFieldLabel('registration_date', FL.registration_date)}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              className="xl:col-span-2"
            />

            {/* 페이지당 항목 수 */}
            <div>
              <label className="form-label">페이지당 항목 수</label>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="select"
              >
                <option value={10}>10개</option>
                <option value={20}>20개</option>
                <option value={50}>50개</option>
                <option value={100}>100개</option>
              </select>
            </div>

            {/* 조회 버튼 */}
            <div className="flex justify-end">
              <Button type="button" variant="primary" onClick={handleQuery}>
                조회
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* 계약 목록 */}
      <div className="card">
        <div className="card-header">
          <div className="flex-between">
            <h2 className="text-lg font-semibold text-gray-900">
              계약 목록 ({data?.totalItems || 0}건)
            </h2>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                {/* <Filter size={16} /> */}
                
              </div>
              {data?.data && data.data.length > 0 && (
                <button
                  onClick={handleExcelDownload}
                  className="btn btn-outline"
                  title="엑셀 다운로드"
                >
                  <Download size={16} />
                  엑셀 다운로드
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="card-body">
          {data?.data?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table" style={{ borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#0b1f3a' }}>
                  <tr>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{getFieldLabel('member_number', FL.member_number)}</th>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{getFieldLabel('tomb_type', FL.tomb_type)}</th>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{getFieldLabel('number_of_tombs', FL.number_of_tombs)}</th>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{`${getFieldLabel('dead_name', FL.dead_name)}1`}</th>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{`${getFieldLabel('dead_name', FL.dead_name)}2`}</th>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{getFieldLabel('mausoleum_location', FL.mausoleum_location)}</th>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{`${getFieldLabel('acquaintance_name', FL.acquaintance_name)}1`}</th>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{`${getFieldLabel('acquaintance_name', FL.acquaintance_name)}2`}</th>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{getFieldLabel('registration_date', FL.registration_date)}</th>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{getFieldLabel('management_period', FL.management_period)}</th>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{getFieldLabel('area_size', FL.area_size)}</th>
                    <th style={{ backgroundColor: '#0b1f3a', color: '#ffffff' }}>{getFieldLabel('payment_status', FL.payment_status)}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((contract) => {
                    // 묘역위치 생성: ${complex}-${area}-${row_number}-${number}
                    const mausoleumNumber = contract.mausoleum ? 
                      `${contract.mausoleum.complex || ''}-${contract.mausoleum.area || ''}-${contract.mausoleum.row_number || ''}-${contract.mausoleum.number || ''}` : 
                      '-';
                    
                    // 사망자 정보 (최대 2명)
                    const deads = contract.deads || [];
                    const dead1 = deads[0] || null;
                    const dead2 = deads[1] || null;
                    
                    // 연고자 정보 (최대 2명)
                    const acquaintances = contract.acquaintances || [];
                    const acquaintance1 = acquaintances[0] || null;
                    const acquaintance2 = acquaintances[1] || null;
                    
                    // 관리비 납부 여부 및 관리일 계산
                    const managementFees = contract.managementFees || [];
                    let paymentStatus = '미발생';
                    let managementPeriodDisplay = '-';
                    
                    // 관리 기간 (묘소 정보에서 가져옴)
                    const managementPeriod = parseInt(contract.mausoleum?.period) || 0;

                    if (managementFees.length > 0) {
                      // 가장 최신 관리비 시작일 찾기
                      const sortedFees = [...managementFees].sort((a, b) =>
                        new Date(b.fee_period_start) - new Date(a.fee_period_start)
                      );
                      const latestFee = sortedFees[0];
                      const startDate = new Date(latestFee.fee_period_start);

                      // 종료일 계산: 시작일 + 관리 기간(년)
                      const endDate = new Date(startDate);
                      endDate.setFullYear(endDate.getFullYear() + managementPeriod);

                      // 관리일: 마지막 관리비 시작일 ~ 종료일
                      managementPeriodDisplay = `${formatDate(latestFee.fee_period_start)} ~ ${formatDate(endDate)}`;
                    
                      // 납부 여부 확인
                      const unpaidFees = managementFees.filter(fee => !fee.is_paid);
                      if (unpaidFees.length === 0) {
                        paymentStatus = '완료';
                      } else {
                        paymentStatus = '미납';
                      }
                    } else if (contract.contract_date) {
                      // 관리비가 없는 경우: 계약일 ~ 계약일 + 관리 기간
                      const startDate = new Date(contract.contract_date);
                      const endDate = new Date(startDate);
                      endDate.setFullYear(endDate.getFullYear() + managementPeriod);

                      managementPeriodDisplay = `${formatDate(contract.contract_date)} ~ ${formatDate(endDate)}`;
                    }
                    
                    // high_level_contract가 있는 경우 (안치자 추가 계약)
                    const isAdditionalBurial = contract.is_additional_burial || contract.high_level_contract;
                    const targetContractId = isAdditionalBurial ? contract.parent_contract_id || contract.high_level_contract : contract.id;
                    const memberNumber = isAdditionalBurial 
                      ? contract.mausoleum?.member_number || '-' // 이미 포맷팅된 묘적번호 (백엔드에서 처리)
                      : contract.mausoleum?.member_number || '-';
                    
                    return (
                      <tr 
                        key={contract.id}
                        onClick={() => navigate(`/contracts/${targetContractId}`)}
                        className="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                      >
                        <td className="font-medium" style={{ border: '1px solid #0000001a' }}>{memberNumber}</td>
                        <td style={{ border: '1px solid #0000001a' }}>{contract.mausoleum?.tomb_type || '-'}</td>
                        <td style={{ border: '1px solid #0000001a' }}>{contract.mausoleum?.number_of_tombs ?? '-'}</td>
                        <td style={{ border: '1px solid #0000001a' }}>{dead1?.name || '-'}</td>
                        <td style={{ border: '1px solid #0000001a' }}>{dead2?.name || '-'}</td>
                        <td className="font-medium" style={{ border: '1px solid #0000001a' }}>{mausoleumNumber}</td>
                        <td style={{ border: '1px solid #0000001a' }}>{acquaintance1?.name || '-'}</td>
                        <td style={{ border: '1px solid #0000001a' }}>{acquaintance2?.name || '-'}</td>
                        <td style={{ border: '1px solid #0000001a' }}>{formatDate(contract.contract_date)}</td>
                        <td style={{ border: '1px solid #0000001a' }}>{managementPeriodDisplay}</td>
                        <td style={{ border: '1px solid #0000001a' }}>{contract.mausoleum?.area_size ? parseFloat(contract.mausoleum.area_size).toFixed(1) : '-'}</td>
                        <td style={{ border: '1px solid #0000001a' }}>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${paymentStatus === '완료' ? 'bg-green-100 text-green-800' :
                            paymentStatus === '미납' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {paymentStatus}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Search size={48} className="mx-auto mb-4 text-gray-300" />
              <p>검색 조건에 맞는 계약이 없습니다.</p>
            </div>
          )}
        </div>

        {/* 페이지네이션 */}
        {data && data.totalPages > 1 && (
          <div className="card-footer">
            <div className="pagination">
              <div className="pagination-info">
                {/* 🔧 디버깅: 페이지네이션 데이터 상태 확인 */}
                {console.log('=== 페이지네이션 데이터 디버깅 ===')}
                {console.log('data:', data)}
                {console.log('data.currentPage:', data.currentPage, '타입:', typeof data.currentPage)}
                {console.log('data.limit:', data.limit, '타입:', typeof data.limit)}
                {console.log('data.totalItems:', data.totalItems, '타입:', typeof data.totalItems)}
                {console.log('계산 결과:', {
                  start: (data.currentPage - 1) * data.limit + 1,
                  end: Math.min(data.currentPage * data.limit, data.totalItems)
                })}
                
                {/* 🔧 안전한 페이지네이션 계산 */}
                {/* 총 {data.totalItems || 0}건 중 {
                  (() => {
                    const currentPage = data.currentPage || 1;
                    const limit = data.limit || 10;
                    const start = (currentPage - 1) * limit + 1;
                    return start;
                  })()
                }-
                {(() => {
                  const currentPage = data.currentPage || 1;
                  const limit = data.limit || 10;
                  const end = Math.min(currentPage * limit, data.totalItems || 0);
                  return end;
                })()}건 */}
                <span>총 {data?.totalPages || 1} 페이지</span>
              </div>
              <div className="pagination-nav">
                <button
                  onClick={() => handlePageChange(data.currentPage - 1)}
                  disabled={data.currentPage === 1}
                  className="pagination-button"
                >
                  이전
                </button>
                {Array.from({ length: Math.min(5, data.totalPages) }, (_, i) => {
                  const page = Math.max(1, Math.min(data.totalPages - 4, data.currentPage - 2)) + i;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`pagination-button ${page === data.currentPage ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => handlePageChange(data.currentPage + 1)}
                  disabled={data.currentPage === data.totalPages}
                  className="pagination-button"
                >
                  다음
                </button>
              </div>
            
            </div>
          </div>
        )}
      </div>

      {/* 안치자 추가 계약 검색 모달 */}
      {showAdditionalBurialModal && (
        <AdditionalBurialSearchModal
          onClose={() => setShowAdditionalBurialModal(false)}
          onSelect={(contractId) => {
            setShowAdditionalBurialModal(false);
            navigate(`/contracts/additional-burial/${contractId}`);
          }}
          getFieldLabel={getFieldLabel}
        />
      )}
    </div>
  );
};

// 안치자 추가 계약 검색 모달 컴포넌트
const AdditionalBurialSearchModal = ({ onClose, onSelect, getFieldLabel }) => {
  const [searchField, setSearchField] = useState('member_number');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async () => {
    const params = {
      member_number: '',
      dead_name: '',
      mausoleum_location: '',
      acquaintance_name: ''
    };

    if (searchTerm.trim()) {
      params[searchField] = searchTerm.trim();
    }

    setIsSearching(true);
    try {
      const response = await transactionAPI.searchForAdditionalBurial(params);
      setSearchResults(response.data.transactions || []);
    } catch (error) {
      console.error('계약 검색 실패:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelect = (contract) => {
    onSelect(contract.id);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">안치자 추가 계약 검색</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>

          {/* 검색 폼 */}
          <div className="mb-6">
            <label className="form-label">검색</label>
            <div className="flex flex-col md:flex-row md:items-center gap-2.5">
              <select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                className="select flex-1 min-w-0"
              >
                <option value="member_number">묘적번호</option>
                <option value="dead_name">망인명</option>
                <option value="mausoleum_location">묘역위치</option>
                <option value="acquaintance_name">연고자</option>
              </select>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input flex-1 min-w-0"
                placeholder={
                  searchField === 'member_number' ? '묘적번호 입력' :
                  searchField === 'dead_name' ? '망인명 입력' :
                  searchField === 'mausoleum_location' ? '묘역위치 입력' :
                  '연고자명 입력'
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
              <Button
                onClick={handleSearch}
                disabled={isSearching}
                className="md:w-28 w-full md:flex-none"
                style={{ minWidth: '112px', height: '46px' }}
              >
                {isSearching ? '검색 중...' : '검색'}
              </Button>
            </div>
          </div>

          {/* 검색 결과 */}
          {searchResults.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{getFieldLabel('member_number', FL.member_number)}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{getFieldLabel('dead_name', FL.dead_name)}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{getFieldLabel('mausoleum_location', FL.mausoleum_location)}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{getFieldLabel('acquaintance_name', FL.acquaintance_name)}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{getFieldLabel('contract_date', FL.contract_date)}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">작업</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {searchResults.map((contract) => {
                    const mausoleumNumber = contract.mausoleum
                      ? `${contract.mausoleum.complex || ''}-${contract.mausoleum.area || ''}-${contract.mausoleum.row_number || ''}-${contract.mausoleum.number || ''}`
                      : '';
                    const deadName = contract.deads?.[0]?.name || '';
                    const acquaintanceName = contract.deads?.[0]?.acquaintance1?.name || 
                                            contract.deads?.[0]?.acquaintance2?.name || '';
                    
                    return (
                      <tr key={contract.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{contract.mausoleum?.member_number || ''}</td>
                        <td className="px-4 py-3 text-sm">{deadName}</td>
                        <td className="px-4 py-3 text-sm">{mausoleumNumber}</td>
                        <td className="px-4 py-3 text-sm">{acquaintanceName}</td>
                        <td className="px-4 py-3 text-sm">{formatDate(contract.contract_date)}</td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => handleSelect(contract)}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            선택
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : searchResults.length === 0 && !isSearching ? (
            <div className="text-center py-8 text-gray-500">
              <p>검색 결과가 없습니다.</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Contracts;